import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../services/api';

// Module-level flag to prevent duplicate auth checks on mount
let authInitialized = false;

/**
 * Hook for authentication initialization and management.
 * Call this once at the app root to initialize auth state from cookies.
 */
export function useAuth() {
  const { setUser, setLoading, logout, isLoading, isAuthenticated, user } = useAuthStore();
  const loggedOut = useRef(false);

  /**
   * Check if user is authenticated by calling /auth/me.
   * The httpOnly cookie will be sent automatically.
   */
  const checkAuth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authApi.me();
      setUser({
        id: data.user.id,
        email: data.user.email,
        firstName: data.user.first_name,
        lastName: data.user.last_name,
        role: data.user.role,
      });
    } catch {
      // Not authenticated or token expired
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading]);

  /**
   * Initialize auth on mount - only once per app lifecycle.
   * After logout, re-check is triggered via the loggedOut ref.
   */
  useEffect(() => {
    if (!authInitialized) {
      authInitialized = true;
      checkAuth();
    }
  }, [checkAuth]);

  /**
   * Handle logout - call API, clear state, then re-verify with server.
   * The re-verify ensures that if cookies weren't properly cleared,
   * we still show the correct auth state.
   */
  const handleLogout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors, still logout locally
    }
    logout();
    loggedOut.current = true;
    // Re-verify auth state with server after cookie deletion.
    // If cookie was cleared, /auth/me will 401 and setUser(null).
    // If cookie persists, user will be re-authenticated (shouldn't happen).
    authInitialized = false;
    await checkAuth();
  }, [logout, checkAuth]);

  return {
    isLoading,
    isAuthenticated,
    user,
    checkAuth,
    logout: handleLogout,
  };
}

/**
 * Hook for components that need auth state but don't need to initialize it.
 */
export function useAuthState() {
  return useAuthStore((state) => ({
    isLoading: state.isLoading,
    isAuthenticated: state.isAuthenticated,
    user: state.user,
  }));
}
